import React from 'react';
import { Button } from '../../../../../components/ui/button';
import { Badge } from '../../../../../components/ui/badge';
import {
  CheckCircle2,
  FileCheck,
  FileSpreadsheet,
  Database,
  Layers,
  RefreshCw,
  Eye,
  Activity,
  FileCode,
  Zap,
  Shield,
} from 'lucide-react';
import type { IngestionResult, Step } from '../types';

interface CompletedPhaseProps {
  ingestionResult: IngestionResult;
  steps: Step[];
  currentStep: number;
  onReset: () => void;
  onViewTemplate: () => void;
}

const processingSteps = ['Text Extraction', 'Structure Analysis', 'AI Normalization', 'Template Creation', 'Field Generation', 'Validation'];

export const CompletedPhase: React.FC<CompletedPhaseProps> = ({
  ingestionResult,
  steps,
  currentStep,
  onReset,
  onViewTemplate,
}) => {
  return (
    <div className="p-3 lg:p-4 max-w-[1400px] mx-auto space-y-3">
      {/* Row 1: Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-500 rounded-xl">
            <CheckCircle2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900">Template Created Successfully</h1>
            <p className="text-xs text-gray-500">Processed and structured by Vastabloc Document Intelligence</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 mr-2">
            {steps.map((step, idx) => (
              <div
                key={step.id}
                className={`w-2 h-2 rounded-full ${idx <= currentStep ? 'bg-green-500' : 'bg-gray-300'}`}
                title={step.label}
              />
            ))}
          </div>
          <Badge variant="success" className="flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Complete
          </Badge>
        </div>
      </div>

      {/* Row 2: Stats strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white border border-green-200 rounded-lg p-3 flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center shrink-0">
            <FileCheck className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <p className="text-xl font-bold text-green-700">{ingestionResult.nodesCreated}</p>
            <p className="text-[10px] text-gray-500">Nodes created</p>
          </div>
        </div>
        <div className="bg-white border border-blue-200 rounded-lg p-3 flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
            <FileSpreadsheet className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-xl font-bold text-blue-700">{ingestionResult.fieldsCreated || 0}</p>
            <p className="text-[10px] text-gray-500">Fields generated</p>
          </div>
        </div>
        <div className="bg-white border border-amber-200 rounded-lg p-3 flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center shrink-0">
            <Database className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="text-xl font-bold text-amber-700">#{ingestionResult.framework.id}</p>
            <p className="text-[10px] text-gray-500">Framework</p>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-3">
          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center shrink-0">
            <Layers className="w-5 h-5 text-gray-600" />
          </div>
          <div>
            <p className="text-xl font-bold text-gray-700">#{ingestionResult.template.id}</p>
            <p className="text-[10px] text-gray-500">Template</p>
          </div>
        </div>
      </div>

      {/* Row 3: Details + Sidebar */}
      <div className="flex flex-col lg:flex-row gap-3">
        {/* Left */}
        <div className="flex-1 min-w-0 space-y-3">
          {/* Template Details */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5 text-blue-600" />
              Template Details
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                <div className="flex items-center gap-1.5 mb-1">
                  <Database className="w-3 h-3 text-blue-600" />
                  <span className="text-[10px] font-medium text-blue-600 uppercase">Framework</span>
                </div>
                <p className="text-sm font-semibold text-blue-900 truncate" title={ingestionResult.framework.name}>
                  {ingestionResult.framework.name}
                </p>
                <Badge variant="info" className="mt-1.5 text-[10px]">ID: {ingestionResult.framework.id}</Badge>
              </div>
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                <div className="flex items-center gap-1.5 mb-1">
                  <Activity className="w-3 h-3 text-amber-600" />
                  <span className="text-[10px] font-medium text-amber-600 uppercase">Version</span>
                </div>
                <p className="text-sm font-semibold text-amber-900">{ingestionResult.version.version}</p>
                <Badge variant="warning" className="mt-1.5 text-[10px]">ID: {ingestionResult.version.id}</Badge>
              </div>
              <div className="p-3 rounded-lg bg-green-50 border border-green-200">
                <div className="flex items-center gap-1.5 mb-1">
                  <FileCode className="w-3 h-3 text-green-600" />
                  <span className="text-[10px] font-medium text-green-600 uppercase">Template</span>
                </div>
                <p className="text-sm font-semibold text-green-900 truncate" title={ingestionResult.template.name}>
                  {ingestionResult.template.name}
                </p>
                <Badge variant="success" className="mt-1.5 text-[10px]">ID: {ingestionResult.template.id}</Badge>
              </div>
            </div>
          </div>

          {/* Processing Summary + Actions — same row */}
          <div className="flex items-center gap-3">
            <div className="flex flex-wrap gap-1.5 flex-1">
              {processingSteps.map((step) => (
                <div key={step} className="flex items-center gap-1 px-2 py-1 bg-green-50 border border-green-200 rounded-md">
                  <CheckCircle2 className="w-3 h-3 text-green-600" />
                  <span className="text-[10px] text-green-800">{step}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button onClick={onReset} variant="outline" className="flex-1">
              <RefreshCw className="w-4 h-4 mr-2" />
              Process Another Document
            </Button>
            <Button
              onClick={onViewTemplate}
              className="flex-1 bg-blue-600 hover:bg-blue-700 shadow-md"
            >
              <Eye className="w-4 h-4 mr-2" />
              View Template
            </Button>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="w-full lg:w-52 shrink-0 space-y-2">
          {/* Success Status */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg shrink-0">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs font-semibold text-green-900">Template Ready</p>
              <p className="text-[10px] text-green-700">Available in library</p>
            </div>
          </div>

          {/* Quick Summary */}
          <div className="bg-white border border-gray-200 rounded-lg p-2.5">
            <h4 className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Created</h4>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">Nodes</span>
                <span className="font-bold text-gray-700">{ingestionResult.nodesCreated}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">Fields</span>
                <span className="font-bold text-gray-700">{ingestionResult.fieldsCreated || 0}</span>
              </div>
            </div>
          </div>

          {/* Engine */}
          <div className="bg-slate-50 border border-indigo-200/50 rounded-lg p-2.5">
            <div className="flex items-center gap-1.5">
              <Zap className="w-3 h-3 text-indigo-600" />
              <span className="text-[10px] font-bold text-indigo-700">Vastabloc</span>
              <span className="text-[9px] text-gray-400 ml-auto">Yiontech</span>
            </div>
            <div className="flex items-center gap-1 mt-1.5 text-[9px] text-green-600">
              <Shield className="w-2.5 h-2.5" />
              <span>Encrypted pipeline</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
